import { DefaultService, TermOrCondition } from "@/api/SolarCloudApi";
import { MutationConfig } from "@/lib/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import getTermsAndConditionsQueryOptions from "./useTermsAndConditions";
import getTrackerEventsQueryOptions from "../tracker/useTrackerEvents";

async function updateTermOrCondition(termOrCondition: TermOrCondition) {
  let response;
  if (termOrCondition.accepted)
    response = await DefaultService.termsAndConditionsAccept(
      termOrCondition.uuid!
    );
  else
    response = await DefaultService.termsAndConditionsReject(
      termOrCondition.uuid!
    );
  return response;
}

type UseUpdateTermOrConditionOptions = {
  mutationConfig?: MutationConfig<typeof updateTermOrCondition>;
};

type MutationContext = {
  previousTermsAndConditions?: TermOrCondition[];
};

export function useUpdateTermOrCondition({
  mutationConfig,
}: UseUpdateTermOrConditionOptions = {}) {
  const queryClient = useQueryClient();

  const { onSuccess, onError, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (data, ...args) => {
      queryClient.invalidateQueries({
        queryKey: getTermsAndConditionsQueryOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getTrackerEventsQueryOptions().queryKey,
      });
      onSuccess?.(data, ...args);
    },
    onMutate: async (termOrCondition: TermOrCondition) => {
      await queryClient.cancelQueries({
        queryKey: getTermsAndConditionsQueryOptions().queryKey,
      });

      const previousTermsAndConditions = queryClient.getQueryData(
        getTermsAndConditionsQueryOptions().queryKey
      );

      queryClient.setQueryData(
        getTermsAndConditionsQueryOptions().queryKey,
        (old: TermOrCondition[] | undefined) => {
          if (!old) return old;
          return old.map((item) =>
            item.uuid === termOrCondition.uuid
              ? { ...item, accepted: termOrCondition.accepted }
              : item
          );
        }
      );

      return { previousTermsAndConditions };
    },
    onError: (err, termOrCondition, context) => {
      const safeContext = context as MutationContext;

      queryClient.setQueryData(
        getTermsAndConditionsQueryOptions().queryKey,
        safeContext.previousTermsAndConditions
      );
      onError?.(err, termOrCondition, context);
    },
    ...restConfig,
    mutationFn: updateTermOrCondition,
  });
}
